import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import Swal from 'sweetalert2';

function Headwisereport() {
  const [heads, setHeads] = useState([]);
  const [selectedHead, setSelectedHead] = useState("");
  const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);

  // 1. Monitor Auth State
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) setUserId(user.uid);
    });
    return () => unsub();
  }, []);

  // 2. Fetch Expense Heads for Dropdown
  useEffect(() => {
    if (!userId) return;
    const fetchHeads = async () => {
      const q = query(collection(db, "expense_heads"), where("userId", "==", userId));
      const snap = await getDocs(q);
      setHeads(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    fetchHeads();
  }, [userId]);

  // 3. Generate Report
  const fetchReport = async () => {
    if (!userId || !selectedHead) {
      Swal.fire("Info", "Please select an Expense Head first", "info");
      return;
    }
    setLoading(true);
    try {
      const q = query(
        collection(db, "daily_expenses"),
        where("userId", "==", userId),
        where("headId", "==", selectedHead),
        where("date", ">=", fromDate),
        where("date", "<=", toDate),
        orderBy("date", "desc")
      );

      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setReportData(data);
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Could not fetch report data", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  // Find the name of the selected head for the title
  const currentHeadName = heads.find(h => h.id === selectedHead)?.headName || "General Expense";

  return (
    <div className="content-wrapper bg-white min-vh-100 text-dark">
      {/* FILTER SECTION (HIDDEN ON PRINT) */}
      <section className="content-header no-print p-4 bg-light border-bottom">
        <div className="row align-items-end">
          <div className="col-md-3">
            <label className="small font-weight-bold">SELECT EXPENSE HEAD</label>
            <select 
              className="form-control border-dark"
              value={selectedHead} 
              onChange={(e) => setSelectedHead(e.target.value)}
            >
              <option value="">-- Choose Category --</option>
              {heads.map(h => <option key={h.id} value={h.id}>{h.headName}</option>)}
            </select>
          </div>
          <div className="col-md-2">
            <label className="small font-weight-bold">FROM DATE</label>
            <input type="date" className="form-control border-dark" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div className="col-md-2">
            <label className="small font-weight-bold">TO DATE</label>
            <input type="date" className="form-control border-dark" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <div className="col-md-4">
            <button className="btn btn-navy px-4 mr-2" onClick={fetchReport} disabled={loading}>
              {loading ? 'Processing...' : 'Generate Report'}
            </button>
            <button className="btn btn-outline-dark" onClick={handlePrint}>Print Report</button>
          </div>
        </div>
      </section>

      {/* REPORT CONTENT */}
      <section className="content p-4 report-container bg-white">
        <div className="text-center mb-4 border-bottom border-dark pb-3">
          <h2 className="font-weight-bold mb-0">SHRIMATI PADMAVATI PETROLEUM</h2>
          <h5 className="text-uppercase mt-2 font-weight-bold">Head-wise Expense Statement</h5>
          <h6 className="text-primary font-weight-bold mt-1">Category: {currentHeadName}</h6>
          <div className="font-weight-bold">Period: {fromDate} to {toDate}</div>
        </div>

        <div className="table-responsive">
          <table className="table table-bordered border-dark">
            <thead className="bg-light">
              <tr className="text-center border-thick">
                <th width="150">DATE</th>
                <th>PAY MODE</th>
                <th>DESCRIPTION / REMARKS</th>
                <th width="200" className="text-right">AMOUNT (₹)</th>
              </tr>
            </thead>
            <tbody>
              {reportData.length > 0 ? reportData.map((row) => (
                <tr key={row.id}>
                  <td className="text-center font-weight-bold">{row.date}</td>
                  <td className="text-center">{row.payMode}</td>
                  <td>{row.description || '--'}</td>
                  <td className="text-right font-weight-bold">₹ {parseFloat(row.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="4" className="text-center py-5">Select a category and dates to view data.</td>
                </tr>
              )}
            </tbody>
            {reportData.length > 0 && (
              <tfoot className="font-weight-bold bg-light border-thick">
                <tr>
                  <td colSpan="3" className="text-right py-2">TOTAL {currentHeadName.toUpperCase()} EXPENSE:</td>
                  <td className="text-right py-2">
                    ₹ {reportData.reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* PRINT SIGNATURES */}
        <div className="only-print mt-5 d-flex justify-content-between px-5">
          <div className="text-center font-weight-bold border-top border-dark pt-2" style={{width: '200px'}}>Accountant</div>
          <div className="text-center font-weight-bold border-top border-dark pt-2" style={{width: '200px'}}>Owner Signature</div>
        </div>
      </section>

      <style dangerouslySetInnerHTML={{ __html: `
        .btn-navy { background: #0f172a; color: white; }
        .btn-navy:hover { background: #1e293b; color: white; }
        .border-thick { border-top: 2px solid #000 !important; border-bottom: 2px solid #000 !important; }
        .table-bordered td, .table-bordered th { border: 1px solid #000 !important; }
        
        @media print {
          .no-print { display: none !important; }
          .report-container { width: 100%; margin-top: 0; }
          .table { font-size: 11pt !important; }
          @page { size: portrait; margin: 1cm; }
        }
        .only-print { display: none; }
        @media print { .only-print { display: flex; } }
      `}} />
    </div>
  );
}

export default Headwisereport;